<template>
    <div>
        <!-- Password prompt -->
        <b-modal
            v-model="showPasswordPrompt"
            size="lg"
            title="Confirm your password"
            modal-class="vue-need-bootstrap"
            lazy
            centered
            hide-footer
        >
            <div v-show="passwordPromptMode === 'show'">Please, confirm your current password to proceed:</div>

            <div v-show="passwordPromptMode === 'generate'">
                <b>WARNING: Generating a new API Key will invalidate your current one!</b>
            </div>

            <validation-observer v-slot="{ handleSubmit, valid }">
                <b-form @submit.stop.prevent="handleSubmit(handlePasswordSubmit)">
                    <!-- Password -->
                    <validation-provider v-slot="{ valid, errors }" rules="required|min:8" name="Password">
                        <b-form-group
                            id="password-input"
                            label-cols="3"
                            content-cols="9"
                            label="Password"
                            label-for="password-input"
                            class="my-3"
                            :invalid-feedback="invalidPassword ? 'Wrong password' : errors[0]"
                            :state="invalidPassword ? false : errors[0] ? false : valid ? true : null"
                        >
                            <b-form-input
                                v-model="password"
                                type="password"
                                :state="invalidPassword ? false : errors[0] ? false : valid ? true : null"
                                :disabled="loadingApiKey"
                            />
                        </b-form-group>
                    </validation-provider>

                    <div v-show="fatalError" class="error-message w-100 pb-3 text-center">Error retrieving API Key</div>

                    <div class="modal-footer row pb-0">
                        <b-button type="reset" :disabled="loadingApiKey" @click="showPasswordPrompt = false">
                            Cancel
                        </b-button>
                        <loader-btn
                            variant="primary"
                            type="submit"
                            loading-text="Loading"
                            :loading="loadingApiKey"
                            :disabled="!valid || loadingApiKey"
                        >
                            {{ passwordPromptMode === 'show' ? 'Show' : 'Generate' }}
                        </loader-btn>
                    </div>
                </b-form>
            </validation-observer>
        </b-modal>

        <b-form-group id="api-key-input" label="API Key" label-for="api-key-input">
            <b-input-group>
                <b-form-input
                    id="api-key-input"
                    v-model="apiKey"
                    :type="inputType"
                    required
                    readonly
                    style="cursor: auto"
                />
                <b-input-group-append>
                    <b-btn variant="primary" :disabled="!canShowApiKey" @click="showApiKey()">Show</b-btn>
                </b-input-group-append>
            </b-input-group>
            <b-form-text v-if="allowRegenerate && user" class="text-right link">
                <a
                    href="javascript:void(0)"
                    :class="{ disabled: loadingApiKey }"
                    @click="!user.ImpersonatedFromAdminUser ? promptPassword('generate') : generateApiKey()"
                >
                    Generate new API Key
                </a>
            </b-form-text>
        </b-form-group>
    </div>
</template>

<script lang="ts" setup>
import { Ref, computed, ref } from 'vue';
import store from '@/store';
import { AxiosError } from 'axios';
import { generateApiKey as _generateApiKey, getApiKeys } from '@/api/user';
import { ValidationProvider, ValidationObserver } from 'vee-validate';
import LoaderBtn from '@/components/LoaderBtn/LoaderBtn.vue';

withDefaults(
    defineProps<{
        allowRegenerate?: boolean;
    }>(),
    {
        allowRegenerate: true
    }
);

const apiKey: Ref<string | null> = ref('sampleapikey');
const loadingApiKey = ref(false);
const invalidPassword = ref(false);
const fatalError = ref(false);

const showPasswordPrompt = ref(false);
const passwordPromptMode: Ref<'show' | 'generate'> = ref('show');
const password = ref('');

const user = computed(() => {
    return store.state.user;
});

const inputType = computed(() => {
    return apiKey.value !== 'sampleapikey' ? 'text' : 'password';
});

const canShowApiKey = computed(() => {
    return apiKey.value === 'sampleapikey';
});

const handlePasswordSubmit = async () => {
    let success = false;
    if (passwordPromptMode.value === 'show') {
        success = await loadApiKey();
    } else if (passwordPromptMode.value == 'generate') {
        success = await generateApiKey();
    }

    if (success) {
        showPasswordPrompt.value = false;
    }
};

const showApiKey = () => {
    // Users impersonated from admin users do not need a password
    if (user.value?.ImpersonatedFromAdminUser) {
        password.value = '';
        loadApiKey();
    } else {
        promptPassword('show');
    }
};

const promptPassword = (mode: 'show' | 'generate') => {
    passwordPromptMode.value = mode;
    password.value = '';
    invalidPassword.value = false;
    showPasswordPrompt.value = true;
};

const generateApiKey = async (): Promise<boolean> => {
    let success = true;
    try {
        fatalError.value = false;
        loadingApiKey.value = true;
        const result = await _generateApiKey(password.value);
        apiKey.value = result.Key;
    } catch (error) {
        const axiosError = error as AxiosError;
        success = false;
        if (axiosError.response?.status === 400 && axiosError.response.data.Message) {
            if (axiosError.response.data.Message === 'Invalid Password') {
                invalidPassword.value = true;
            }
        } else {
            window.rumReportError(axiosError);
            fatalError.value = true;
        }
    } finally {
        loadingApiKey.value = false;
    }

    return success;
};

const loadApiKey = async (): Promise<boolean> => {
    let success = true;
    try {
        fatalError.value = false;
        loadingApiKey.value = true;
        const keys = await getApiKeys(password.value);
        apiKey.value = keys[0]?.Key || 'No API Key';
    } catch (error) {
        const axiosError = error as AxiosError;
        success = false;
        if (axiosError.response?.status === 400 && axiosError.response.data.Message) {
            if (axiosError.response.data.Message === 'Invalid Password') {
                invalidPassword.value = true;
            }
        } else {
            window.rumReportError(axiosError);
            fatalError.value = true;
        }
    } finally {
        loadingApiKey.value = false;
    }

    return success;
};
</script>

<style lang="scss" scoped>
.input-group {
    min-width: 400px;
}
</style>
